import type { WavespeedModelConfig } from '../types';
import { clampTo, hasImageInput } from '../../tools';

export const miscConfigs: Record<string, WavespeedModelConfig> = {
    // SeedVR2
    'wavespeed-ai/seedvr2/image': {
        apiPath: '/wavespeed-ai/seedvr2/image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { image_urls, providerOptions } = params;
            return {
                image: image_urls[0],
                target_resolution: (providerOptions as any)?.target_resolution || '4k',
                output_format: ((providerOptions as any)?.output_format === 'jpeg') ? 'jpeg' : 'png',
            };
        },
    },
    // Background Remover
    'wavespeed-ai/image-background-remover': {
        apiPath: '/wavespeed-ai/image-background-remover',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { image_urls } = params;
            return {
                image: image_urls[0],
            };
        },
    },
    // 3D
    'wavespeed-ai/meshy6/text-to-3d': {
        apiPath: '/wavespeed-ai/meshy6/text-to-3d',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt } = params;
            return {
                prompt: prompt,
            };
        },
    },
    // Meta SAM 3 (image segmentation)
    'wavespeed-ai/sam3-image': {
        // https://wavespeed.ai/models/wavespeed-ai/sam3-image
        apiPath: '/wavespeed-ai/sam3-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, providerOptions } = params;
            const hasImage = hasImageInput(image_urls);
            if (!hasImage) {
                throw new Error('Missing Image Input');
            }
            return {
                image: image_urls[0],
                prompt: prompt,
                apply_mask: typeof providerOptions?.apply_mask === 'boolean' ? providerOptions.apply_mask : true,
                output_format: typeof providerOptions?.output_format === 'string' ? providerOptions.output_format : 'png',
            };
        },
    },
    // Topaz Image Enhance / Upscale
    'topaz/image/enhance': {
        // https://wavespeed.ai/models/topaz/image/enhance
        apiPath: '/topaz/image/enhance',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { image_urls, providerOptions } = params;
            const hasImage = hasImageInput(image_urls);
            if (!hasImage) {
                throw new Error('Missing Image Input');
            }
            return {
                image: image_urls[0],
                model: typeof providerOptions?.model === 'string' ? providerOptions.model : 'Standard V2',
                ...(typeof providerOptions?.upscale_factor === 'number' && { upscale_factor: providerOptions.upscale_factor }),
                output_format: (providerOptions as any)?.output_format === 'png' ? 'png' : 'jpeg',
            };
        },
    },
    // Tencent HunyuanImage 3.0 Instruct
    'wavespeed-ai/hunyuan-image-3-instruct/text-to-image': {
        // https://wavespeed.ai/models/wavespeed-ai/hunyuan-image-3-instruct/text-to-image
        apiPath: '/wavespeed-ai/hunyuan-image-3-instruct/text-to-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, aspectRatio, seed } = params;
            return {
                prompt: prompt,
                size: clampTo(aspectRatio || '1:1', ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'] as const, '1:1'),
                ...(typeof seed === 'number' && { seed }),
            };
        },
    },
    'wavespeed-ai/hunyuan-image-3-instruct/edit': {
        // https://wavespeed.ai/models/wavespeed-ai/hunyuan-image-3-instruct/edit
        apiPath: '/wavespeed-ai/hunyuan-image-3-instruct/edit',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, seed } = params;
            const hasImage = hasImageInput(image_urls);
            if (!hasImage) {
                throw new Error('Missing Image Input');
            }
            return {
                prompt: prompt,
                images: image_urls.slice(0, 2),
                size: aspectRatio === 'match' ? undefined : clampTo(aspectRatio || '1:1', ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'] as const, '1:1'),
                ...(typeof seed === 'number' && { seed }),
            };
        },
    },
    // Runway Gen-4 Image
    'runwayml/gen4-image': {
        // https://wavespeed.ai/models/runwayml/gen4-image
        apiPath: '/runwayml/gen4-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution, seed } = params;
            const hasImage = hasImageInput(image_urls);
            return {
                prompt: prompt,
                aspect_ratio: clampTo(aspectRatio || '4:3', ['1:1', '16:9', '9:16', '4:3', '3:4'] as const, '4:3'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1080p', ['1080p', '720p'] as const, '1080p'),
                ...(hasImage && { reference_images: image_urls.slice(0, 3) }),
                ...(typeof seed === 'number' && { seed }),
            };
        },
    },
    'runwayml/gen4-image-turbo': {
        // https://wavespeed.ai/models/runwayml/gen4-image-turbo
        apiPath: '/runwayml/gen4-image-turbo',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution, seed } = params;
            const hasImage = hasImageInput(image_urls);
            if (!hasImage) {
                throw new Error('Missing Image Input');
            }
            return {
                prompt: prompt,
                aspect_ratio: clampTo(aspectRatio || '4:3', ['1:1', '16:9', '9:16', '4:3', '3:4'] as const, '4:3'),
                resolution: clampTo(image_resolution?.toLowerCase() || '1080p', ['1080p', '720p'] as const, '1080p'),
                reference_images: image_urls.slice(0, 3),
                ...(typeof seed === 'number' && { seed }),
            };
        },
    },
};